<script setup>
import { computed } from 'vue';
import { Link, usePage } from '@inertiajs/vue3';

const props = defineProps({
    setting: Object,
});

const waUrl = computed(() => {
    const number = props.setting?.setting_no_wa || '6281234567890';
    return `https://wa.me/${number.replace(/^0/, '62')}?text=Halo,%20saya%20ingin%20tanya%20tentang%20Gotransport`;
});

const page = usePage();
const footerCategories = computed(() => page.props.footer_categories || []);

const currentYear = new Date().getFullYear();
</script>

<template>
    <footer class="relative bg-[#0f172a] text-white pt-20 pb-10 overflow-hidden font-sans">
        <!-- Background Decorations -->
        <div class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 via-red-500 to-purple-600"></div>
        <div class="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-blue-600/5 blur-[120px] pointer-events-none"></div>
        <div class="absolute top-[20%] -right-[10%] w-[40%] h-[40%] rounded-full bg-orange-600/5 blur-[100px] pointer-events-none"></div>

        <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl relative z-10">
            
            <!-- Top Section: CTA & Socials -->
            <div class="flex flex-col md:flex-row items-center justify-between gap-8 mb-16 border-b border-gray-800 pb-12">
                <div class="text-center md:text-left">
                    <h3 class="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400 mb-2">Ikuti Kami di Media Sosial</h3>
                    <p class="text-gray-300 text-sm">Dapatkan update terbaru dan promo menarik.</p>
                </div>
                
                <div class="flex flex-wrap justify-center gap-4">
                    <a v-if="setting?.setting_url_facebook" :href="setting.setting_url_facebook" target="_blank" class="social-btn group bg-[#1e293b] hover:bg-[#1877F2]" aria-label="Facebook Gotransport">
                        <i class="fa fa-facebook text-xl text-gray-400 group-hover:text-white transition-colors" aria-hidden="true"></i>
                    </a>
                    <a v-if="setting?.setting_url_instagram" :href="setting.setting_url_instagram" target="_blank" class="social-btn group bg-[#1e293b] hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-600" aria-label="Instagram Gotransport">
                        <i class="fa fa-instagram text-xl text-gray-400 group-hover:text-white transition-colors" aria-hidden="true"></i>
                    </a>
                    <a v-if="setting?.setting_url_tiktok" :href="setting.setting_url_tiktok" target="_blank" class="social-btn group bg-[#1e293b] hover:bg-black" aria-label="TikTok Gotransport">
                        <i class="fa fa-music text-xl text-gray-400 group-hover:text-white transition-colors" aria-hidden="true"></i>
                    </a>
                    <a v-if="setting?.setting_url_google_maps" :href="setting.setting_url_google_maps" target="_blank" class="social-btn group bg-[#1e293b] hover:bg-red-500" aria-label="Lokasi Gotransport di Google Maps">
                        <i class="fa fa-map-marker text-xl text-gray-400 group-hover:text-white transition-colors" aria-hidden="true"></i>
                    </a>
                </div>
            </div>

            <div class="grid md:grid-cols-12 gap-12 mb-16">
                <!-- Brand & About (4 cols) -->
                <div class="md:col-span-4 space-y-6">
                    <div class="inline-block">
                        <h4 class="text-2xl font-extrabold tracking-tight text-white mb-2">
                            {{ setting?.setting_title || 'Gotransport' }}
                            <span class="text-orange-500">.</span>
                        </h4>
                        <div class="h-1 w-12 bg-orange-500 rounded-full"></div>
                    </div>
                    
                    <p class="text-gray-400 leading-relaxed text-sm text-justify">
                        {{ setting?.setting_meta_description || 'Gotransport adalah penyedia jasa sewa mobil Banyuwangi murah dan terpercaya. Kami melayani rental mobil lepas kunci, sewa mobil dengan supir, dan paket wisata terbaik di Banyuwangi dengan armada lengkap dan pelayanan profesional.' }}
                    </p>
                    
                    <div class="flex items-start gap-4 p-4 rounded-xl bg-gray-800/30 border border-gray-800">
                        <div class="mt-1 min-w-[20px] text-orange-500"><i class="fa fa-map-marker text-lg"></i></div>
                        <span class="text-sm text-gray-300 leading-snug">{{ setting?.setting_alamat }}</span>
                    </div>
                </div>
                
                <!-- Links Grid (8 cols split into 3) -->
                <div class="md:col-span-8 grid sm:grid-cols-3 gap-8">
                    <!-- Layanan -->
                    <div>
                        <h4 class="text-white font-bold text-lg mb-6 flex items-center gap-2">
                            Layanan Rental
                        </h4>
                        <ul class="space-y-3">
                            <li v-for="category in footerCategories" :key="category.id">
                                <Link :href="`/layanan/kategori/${category.slug}`" class="footer-link">
                                    {{ category.name }}
                                </Link>
                            </li>
                            <li v-if="footerCategories.length === 0">
                                <span class="text-gray-500 text-sm">Belum ada kategori</span>
                            </li>
                        </ul>
                    </div>

                    <!-- Informasi -->
                    <div>
                        <h4 class="text-white font-bold text-lg mb-6 flex items-center gap-2">
                            Informasi
                        </h4>
                        <ul class="space-y-3">
                            <li><Link href="/profil" class="footer-link">Tentang Gotransport</Link></li>
                            <li><Link href="/area-layanan" class="footer-link">Area Layanan</Link></li>
                            <li><Link href="/syarat-ketentuan" class="footer-link">Syarat & Ketentuan</Link></li>
                            <li><Link href="/faq" class="footer-link">FAQ</Link></li>
                            <li><Link href="/contact" class="footer-link">Kontak</Link></li>
                        </ul>
                    </div>
                    
                    <!-- Kontak -->
                    <div>
                        <h4 class="text-white font-bold text-lg mb-6 flex items-center gap-2">
                            Hubungi Kami
                        </h4>
                        <ul class="space-y-4">
                            <li>
                                <a :href="`tel:${setting?.setting_no_telp}`" class="contact-item group">
                                    <div class="icon-box group-hover:bg-orange-500 group-hover:text-white">
                                        <i class="fa fa-phone"></i>
                                    </div>
                                    <div class="flex flex-col">
                                        <span class="label">Telepon</span>
                                        <span class="value">{{ setting?.setting_no_telp }}</span>
                                    </div>
                                </a>
                            </li>
                            <li>
                                <a :href="waUrl" target="_blank" class="contact-item group">
                                    <div class="icon-box group-hover:bg-green-500 group-hover:text-white">
                                        <i class="fa fa-whatsapp"></i>
                                    </div>
                                    <div class="flex flex-col">
                                        <span class="label">WhatsApp</span>
                                        <span class="value">{{ setting?.setting_no_wa }}</span>
                                    </div>
                                </a>
                            </li>
                            <li>
                                <a :href="`mailto:${setting?.setting_email}`" class="contact-item group">
                                    <div class="icon-box group-hover:bg-blue-500 group-hover:text-white">
                                        <i class="fa fa-envelope"></i>
                                    </div>
                                    <div class="flex flex-col">
                                        <span class="label">Email</span>
                                        <span class="value break-all">{{ setting?.setting_email }}</span>
                                    </div>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            
            <div class="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
                <p>
                    &copy; 2018 - {{ currentYear }} <strong class="text-white">{{ setting?.setting_title || 'Gotransport' }}</strong>. All Rights Reserved.
                </p>
                <div class="flex gap-6">
                    <Link href="/privacy" class="text-gray-400 hover:text-orange-500 transition-colors">Privacy Policy</Link>
                    <a href="/sitemap.xml" class="text-gray-400 hover:text-orange-500 transition-colors">Sitemap</a>
                </div>
            </div>
        </div>
    </footer>
</template>

<style scoped>
.social-btn {
    @apply w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 border border-gray-700 hover:border-transparent hover:-translate-y-1 shadow-lg;
}

.footer-link {
    @apply text-gray-400 hover:text-orange-500 transition-colors flex items-center gap-2 hover:translate-x-1 duration-200 text-sm;
}
.footer-link::before {
    content: "›";
    @apply text-gray-600 text-lg leading-none transition-colors;
}
.footer-link:hover::before {
    @apply text-orange-500;
}

.contact-item {
    @apply flex items-center gap-3 hover:text-white transition-colors;
}

.icon-box {
    @apply w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center text-gray-400 transition-all duration-300 flex-shrink-0 border border-gray-700/50;
}

.label {
    @apply text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-0.5;
}

.value {
    @apply text-sm text-gray-300 group-hover:text-white transition-colors;
}
</style>
